// CustomerLandingPage.jsx

import React, { useEffect } from 'react';
import { Box, Grid, Text, Paper, Stack, Group } from '@mantine/core';
import { useQuery } from '@apollo/client';
import { GET_CUSTOMER_QUOTE_TO_CLOSE } from '../../queries';
import { GET_CUSTOMER_PROFICIENCY } from '../../queries';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  if (amount === 0) return '$0';
  const roundedAmount = amount < 0
    ? Math.ceil(amount)  // Round up for negative amounts to avoid cutting off cents improperly
    : Math.floor(amount); // Round down for positive amounts
  const absAmount = Math.abs(roundedAmount).toLocaleString();
  return roundedAmount < 0 ? `-$${absAmount}` : `$${absAmount}`;
};

function formatAsPercentage(value) {
  if (value == null) return ''; // Handle null/undefined gracefully
  return `${value.toFixed(1)}%`;
}

const Proficiency = ({ selectedCustomer }) => {
  const { data: quoteToCloseData, loading: quoteLoading, error: quoteError } = useQuery(GET_CUSTOMER_QUOTE_TO_CLOSE, {
    variables: { acctno: selectedCustomer?.acctNo || '' },
  });

  const { data: proficiencyData, loading: proficiencyLoading, error: proficiencyError } = useQuery(GET_CUSTOMER_PROFICIENCY, {
    variables: { acctno: selectedCustomer?.acctNo || '' },
  });

  useEffect(() => {
    if (quoteError) {
      console.error('Error fetching quote-to-close data:', quoteError);
    }
    if (proficiencyError) {
      console.error('Error fetching proficiency data:', proficiencyError);
    }
  }, [quoteError, proficiencyError]);

  if (quoteLoading || proficiencyLoading) return <Text p="xs">Loading...</Text>;
  if (quoteError || proficiencyError) {
    return <Text p="xs" c="red">
      Error: {quoteError?.message || proficiencyError?.message}
    </Text>;
  }
  if (!quoteToCloseData || !proficiencyData) return <Text p="xs">No data available</Text>;

  const quoteToClose = quoteToCloseData.quoteToCloseForCustomer;
  const proficiency = proficiencyData.customerProficiency;

  const InfoItem = ({ label, value, color }) => (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Text size="sm" c={color || "dimmed"} fw={500} style={{ minWidth: 'fit-content' }}>
        {label}:
      </Text>
      <Text size="sm" c={color || "dark"} fw={600} ta="right" style={{ wordBreak: 'break-word' }}>
        {value}
      </Text>
    </Group>
  );

  const Section = ({ title, children, helpText }) => (
    <Paper p="xs" withBorder h="100%">
      <Stack gap="xs" h="100%">
        <Text fw={700} size="md" c="dark">
          {title}
        </Text>
        <Box style={{ flex: 1 }}>
          {children}
        </Box>
        <Text size="xs" c="dimmed" fs="italic">
          {helpText}
        </Text>
      </Stack>
    </Paper>
  );

  return (
    <Box p="xs">
      <Grid gutter="xs">
        {/* Sales vs. Sell Price Section */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Section title="Sales vs. Sell Price" helpText="12 month average">
            <Stack gap={2}>
              <InfoItem
                label="Percentage"
                value={formatAsPercentage(proficiency.salesVsSellPricePc)}
                color={proficiency.salesVsSellPriceTotalDollarDifference < 0 ? "#A90000" : "#00a900"}
              />
              <InfoItem
                label="Total Dollar Difference"
                value={formatCurrency(proficiency.salesVsSellPriceTotalDollarDifference)}
                color={proficiency.salesVsSellPriceTotalDollarDifference < 0 ? "#A90000" : "#00a900"}
              />
              <InfoItem label="total # of sales (lines)" value={proficiency.salesVsSellPriceTotalNoOfSales} />
              <InfoItem label="# of graded sales (lines)" value={proficiency.salesVsSellPriceNoOfGradedSales} />
              <InfoItem
                label="Avg $ per sale"
                value={formatCurrency(proficiency.salesVsSellPriceAvgDollarsPerSale)}
                color={proficiency.salesVsSellPriceTotalDollarDifference < 0 ? "#A90000" : "#00a900"}
              />
            </Stack>
          </Section>
        </Grid.Col>

        {/* Quote to Close Section */}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Section title="Quote to Close" helpText="12 month average">
            <Stack gap={2}>
              <InfoItem
                label="Qty Percentage"
                value={`${formatAsPercentage(quoteToClose.percentQ2CQty12m)} (${quoteToClose.qtySold12m} / ${quoteToClose.qtyQuoted12m})`}
              />
              <InfoItem
                label="Dollars Percentage"
                value={`${formatAsPercentage(quoteToClose.percentQ2CDollars12m)} (${formatCurrency(quoteToClose.dollarsSold12m)} / ${formatCurrency(quoteToClose.dollarsQuoted12m)})`}
              />
            </Stack>
          </Section>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export default Proficiency;